import { useMemo, useRef, useState, useEffect, useId, type ReactNode } from "react";
import {
  flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel,
  getSortedRowModel, useReactTable,
  type ColumnDef, type SortingState, type VisibilityState,
} from "@tanstack/react-table";
import { EmptyState } from "./primitives";

/**
 * The table every data page renders through.
 *
 * Built on the TanStack features that were already installed and unused. The
 * additions here are the ones this data actually needs, not a feature tour:
 *
 * - STICKY IDENTITY COLUMN. Arabic URLs run long, so the column that says WHICH
 *   page a row is scrolls out of view exactly when the numbers you scrolled to
 *   see come into it. Pinning it is the single biggest usability fix for this
 *   dataset, and it is why the wrapper scrolls rather than the page.
 * - COLUMN VISIBILITY, dub's "Display" menu. Ten columns is right for an audit
 *   and wrong for a standup; let the reader choose instead of guessing for them.
 * - MULTI-SORT via shift-click, because "biggest losers, then by impressions"
 *   is a real question and a single sort key cannot express it.
 * - CSV EXPORT. Work leaves this app for writers and spreadsheets. A table you
 *   cannot get data out of forces copy-paste, and copy-paste mangles Arabic.
 *
 * Deliberately NOT virtualized: pagination at 25 rows keeps the DOM small
 * already, and virtualization would fight the sticky header. Revisit only if a
 * surface genuinely needs thousands of rows on one screen.
 */

interface Props<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  searchPlaceholder?: string;
  globalFilterFn?: (row: T, term: string) => boolean;
  initialSort?: SortingState;
  toolbar?: ReactNode;
  emptyHint?: string;
  /** Column id pinned to the leading edge. Defaults to the first column. */
  stickyColumnId?: string;
  /** Basename for the exported file; export is hidden when omitted. */
  exportName?: string;
  /** Disable this client pager when the caller already paginates at the API. */
  paginate?: boolean;
  /** Accessible name for the table and its horizontal scroll region. */
  ariaLabel?: string;
  /** Highlight this row (must match getRowId). */
  selectedId?: string | null;
  getRowId?: (row: T) => string;
  onRowClick?: (row: T) => void;
}

const PAGE_SIZE_DESKTOP = 25;
const PAGE_SIZE_MOBILE = 10;

/** Strips React elements down to something a spreadsheet can hold. */
function cellText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "object") return "";
  return String(value);
}

function toCsv<T>(rows: T[], columns: ColumnDef<T, any>[]): string {
  const cols = columns.filter((c) => (c as any).accessorKey);
  const head = cols.map((c) => `"${String(c.header ?? (c as any).accessorKey)}"`).join(",");
  const body = rows.map((r) =>
    cols.map((c) => {
      const raw = (r as Record<string, unknown>)[(c as any).accessorKey as string];
      // Double the quotes, not escape them — CSV's own rule, and the one that
      // survives Arabic text and commas inside URLs.
      return `"${cellText(raw).replace(/"/g, '""')}"`;
    }).join(","),
  ).join("\n");
  return `${head}\n${body}`;
}

export function DataTable<T>({
  data, columns, searchPlaceholder = "Search…", globalFilterFn,
  initialSort = [], toolbar, emptyHint = "Try widening the period or clearing filters.",
  stickyColumnId, exportName, paginate = true, ariaLabel = "Data table",
  selectedId, getRowId, onRowClick,
}: Props<T>) {
  const [sorting, setSorting] = useState<SortingState>(initialSort);
  const [term, setTerm] = useState("");
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [displayOpen, setDisplayOpen] = useState(false);
  const [pageSize, setPageSize] = useState(() =>
    matchMedia("(max-width: 767px)").matches ? PAGE_SIZE_MOBILE : PAGE_SIZE_DESKTOP,
  );
  const displayRef = useRef<HTMLDivElement>(null);
  const displayButtonRef = useRef<HTMLButtonElement>(null);
  const displayOptionsId = useId();
  const searchRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTableSectionElement>(null);

  // Command-F focuses the table's own search, the way a Mac app scopes Find to
  // the frontmost list instead of the browser's page search.
  useEffect(() => {
    const onFind = (e: KeyboardEvent) => {
      if (e.key !== "f" || !(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return;
      e.preventDefault();
      searchRef.current?.focus();
      searchRef.current?.select();
    };
    document.addEventListener("keydown", onFind);
    return () => document.removeEventListener("keydown", onFind);
  }, []);

  useEffect(() => {
    if (!displayOpen) return;
    const onDown = (e: MouseEvent) => {
      if (displayRef.current && !displayRef.current.contains(e.target as Node)) setDisplayOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setDisplayOpen(false);
      displayButtonRef.current?.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [displayOpen]);

  useEffect(() => {
    const media = matchMedia("(max-width: 767px)");
    const resizePage = () => setPageSize(media.matches ? PAGE_SIZE_MOBILE : PAGE_SIZE_DESKTOP);
    media.addEventListener("change", resizePage);
    return () => media.removeEventListener("change", resizePage);
  }, []);

  const filtered = useMemo(() => {
    if (!term.trim() || !globalFilterFn) return data;
    const t = term.trim().toLowerCase();
    return data.filter((r) => globalFilterFn(r, t));
  }, [data, term, globalFilterFn]);

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(paginate ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    enableMultiSort: true,
    initialState: { pagination: { pageSize } },
  });

  useEffect(() => {
    if (!paginate) return;
    table.setPageSize(pageSize);
    table.setPageIndex(0);
  }, [pageSize, table, paginate]);

  const total = data.length;
  const shown = filtered.length;
  const pinnedId = stickyColumnId ?? (columns[1] as any)?.accessorKey ?? (columns[0] as any)?.accessorKey;

  function download() {
    const csv = toCsv(filtered, columns);
    // BOM first, or Excel opens Arabic as mojibake — the single most common way
    // an export "works" in testing and is unusable for the person who needs it.
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${exportName}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const ctrlBtn: React.CSSProperties = {
    height: "var(--ui-control-h)", padding: "0 10px", cursor: "pointer",
    font: "inherit", fontSize: "var(--ui-text-control)", fontWeight: 500,
    color: "var(--c-t1)", background: "var(--c-canvas)",
    border: "1px solid var(--c-border)", borderRadius: "var(--ui-control-radius)",
    display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
    transition: "background var(--ui-dur-instant) var(--ui-ease)",
  };

  return (
    <div>
      <div style={{ display: "flex", gap: "var(--ui-space-3)", alignItems: "center",
        flexWrap: "wrap", marginBottom: "var(--ui-space-3)" }}>
        {toolbar}
        <input
          ref={searchRef}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          style={{
            height: "var(--ui-control-h)", minWidth: 240, flex: "1 1 240px",
            padding: "0 10px", fontSize: "var(--ui-text-control)",
            border: "1px solid var(--c-border)", borderRadius: "var(--ui-control-radius)",
            background: "var(--c-canvas)", color: "var(--c-t1)",
          }}
        />

        {/* Display — dub's column menu. */}
        <div ref={displayRef} style={{ position: "relative" }}>
          <button ref={displayButtonRef} type="button"
            onClick={() => setDisplayOpen((o) => !o)} style={ctrlBtn}
            aria-expanded={displayOpen} aria-haspopup="dialog" aria-controls={displayOptionsId}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                 strokeWidth="1.5" strokeLinecap="round" aria-hidden>
              <path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h11" /><circle cx="5.5" cy="4.5" r="1.6" fill="currentColor" stroke="none" />
              <circle cx="10.5" cy="11.5" r="1.6" fill="currentColor" stroke="none" />
            </svg>
            Display
          </button>
          {displayOpen && (
            <div id={displayOptionsId} className="ui-enter" role="dialog" aria-label="Visible columns" style={{
              position: "absolute", top: "calc(100% + 6px)", insetInlineEnd: 0, zIndex: 30,
              minWidth: 190, padding: 4, background: "var(--c-material-popover)",
              backdropFilter: "var(--ui-material)", WebkitBackdropFilter: "var(--ui-material)",
              border: "1px solid var(--c-border)", borderRadius: "var(--ui-sheet-radius)",
              boxShadow: "var(--ui-shadow-popover)",
            }}>
              {table.getAllLeafColumns().map((col) => (
                <label key={col.id} style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "6px 8px",
                  fontSize: "var(--ui-text-control)", color: "var(--c-t2)",
                  borderRadius: "var(--ui-control-radius)", cursor: "pointer",
                }}>
                  <input type="checkbox" checked={col.getIsVisible()}
                    onChange={col.getToggleVisibilityHandler()} />
                  {String(col.columnDef.header ?? col.id)}
                </label>
              ))}
            </div>
          )}
        </div>

        {exportName && (
          <button type="button" onClick={download} style={ctrlBtn} title="Download the filtered rows as CSV">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                 strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M8 2.5v8M4.5 7.5L8 11l3.5-3.5M2.5 13.5h11" />
            </svg>
            Export
          </button>
        )}

        {/* Filtering must never hide how much was filtered out. */}
        <span className="tabnum" style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t3)", whiteSpace: "nowrap" }}>
          {shown === total ? `${total.toLocaleString()} rows` : `${shown.toLocaleString()} of ${total.toLocaleString()} rows`}
        </span>
      </div>

      {shown === 0 ? (
        <EmptyState title="No rows match" body={`Nothing matched “${term}”.`} hint={emptyHint} />
      ) : (
        <>
          <div className="ui-tablewrap" role="region" tabIndex={0}
            aria-label={`${ariaLabel} — scroll horizontally for more columns`}
            onKeyDown={(event) => {
              if (!onRowClick) return;
              const rows = table.getRowModel().rows;
              if (rows.length === 0) return;
              const idOf = (r: typeof rows[number]) => {
                const o = r.original as T;
                return getRowId ? getRowId(o) : (o as { id?: string }).id;
              };
              const current = rows.findIndex((r) => idOf(r) === selectedId);
              let next: number | null = null;
              if (event.key === "ArrowDown") next = current < 0 ? 0 : Math.min(current + 1, rows.length - 1);
              else if (event.key === "ArrowUp") next = current < 0 ? rows.length - 1 : Math.max(current - 1, 0);
              else if (event.key === "Home") next = 0;
              else if (event.key === "End") next = rows.length - 1;
              if (next === null) return;
              event.preventDefault();
              onRowClick(rows[next].original as T);
              // Keep the moved selection on screen, as AppKit does.
              bodyRef.current?.children[next]?.scrollIntoView({ block: "nearest" });
            }}
            style={{ border: "1px solid var(--c-border)", borderRadius: "var(--ui-card-radius)" }}>
            <table aria-label={ariaLabel} className="ui-table"
              style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: "var(--ui-text-body)" }}>
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((h) => {
                      const sortable = h.column.getCanSort();
                      const dir = h.column.getIsSorted();
                      const numeric = (h.column.columnDef.meta as any)?.numeric;
                      const pinned = h.column.id === pinnedId;
                      return (
                        <th key={h.id}
                          scope="col"
                          aria-sort={sortable ? (dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none") : undefined}
                          style={{
                            textAlign: numeric ? "right" : "left",
                            padding: 0,
                            fontSize: "var(--ui-text-meta)", fontWeight: 600,
                            
                            color: "var(--c-t2)", background: "var(--c-surface)",
                            borderBottom: "1px solid var(--c-border)",
                            whiteSpace: "nowrap",
                            position: "sticky", top: 0,
                            // The pinned header needs a higher stack order than
                            // the pinned body cells, or rows scroll over it.
                            ...(pinned ? { insetInlineStart: 0, zIndex: 3 } : { zIndex: 2 }),
                          }}>
                          {sortable ? (
                            <button type="button" onClick={h.column.getToggleSortingHandler()}
                              title="Sort this column · Shift-click to add a second sort"
                              style={{
                                appearance: "none", display: "flex", alignItems: "center",
                                justifyContent: numeric ? "flex-end" : "flex-start", gap: 4,
                                width: "100%", padding: "8px 16px",
                                border: 0, color: "inherit", background: "transparent", cursor: "pointer",
                                font: "inherit", fontWeight: "inherit", letterSpacing: "inherit",
                                textTransform: "inherit", whiteSpace: "nowrap",
                              }}>
                              {flexRender(h.column.columnDef.header, h.getContext())}
                              <span aria-hidden>{dir === "asc" ? "▲" : dir === "desc" ? "▼" : ""}</span>
                            </button>
                          ) : (
                            <div style={{ padding: "8px 16px" }}>
                              {flexRender(h.column.columnDef.header, h.getContext())}
                            </div>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody ref={bodyRef}>
                {table.getRowModel().rows.map((row) => {
                  const original = row.original as T;
                  const id = getRowId ? getRowId(original) : (original as { id?: string }).id;
                  const selected = selectedId != null && id === selectedId;
                  return (
                  <tr
                    key={row.id}
                    className={[onRowClick ? "is-clickable" : "", selected ? "is-selected" : ""].filter(Boolean).join(" ")}
                    onClick={onRowClick ? () => onRowClick(original) : undefined}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const numeric = (cell.column.columnDef.meta as any)?.numeric;
                      const pinned = cell.column.id === pinnedId;
                      return (
                        <td key={cell.id}
                          className={numeric ? "tabnum" : undefined}
                          style={{
                            textAlign: numeric ? "right" : "left",
                            padding: "8px 16px",
                            borderBottom: "1px solid var(--c-border)",
                            color: "var(--c-t1)", verticalAlign: "top",
                            ...(pinned ? {
                              position: "sticky", insetInlineStart: 0, zIndex: 1,
                              // Inherit the ROW's solid colour: still opaque over
                              // scrolled columns, and the hover tone cannot break
                              // at the pinned cell.
                              background: "inherit",
                              borderInlineEnd: "1px solid var(--c-border)",
                              maxWidth: 420,
                            } : null),
                          }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {paginate && <div style={{ display: "flex", alignItems: "center", gap: "var(--ui-space-3)", marginTop: "var(--ui-space-3)" }}>
            <button type="button" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}
              style={{ ...pagerBtn, cursor: table.getCanPreviousPage() ? "pointer" : "not-allowed", opacity: table.getCanPreviousPage() ? 1 : .55 }}>Previous</button>
            <span className="tabnum" style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t3)" }}>
              Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
            </span>
            <button type="button" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}
              style={{ ...pagerBtn, cursor: table.getCanNextPage() ? "pointer" : "not-allowed", opacity: table.getCanNextPage() ? 1 : .55 }}>Next</button>
          </div>}
        </>
      )}
    </div>
  );
}

const pagerBtn: React.CSSProperties = {
  height: "var(--ui-control-h-compact)", padding: "0 10px", cursor: "pointer",
  font: "inherit", fontSize: "var(--ui-text-control)",
  border: "1px solid var(--c-border)", borderRadius: "var(--ui-control-radius)",
  background: "var(--c-canvas)", color: "var(--c-t1)",
};
